import React from 'react';
import { styled } from '@stitches/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// 区块容器样式
const SectionContainer = styled('section', {
  width: '100%',
  padding: 'var(--spacing-xxl) +20px',
  backgroundColor: '#FFFFFF',
  position: 'relative',
  overflow: 'hidden',
  
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-xl) +20px'
  }
});

// 内容容器
const ContentContainer = styled('div', {
  maxWidth: 'var(--container-width)',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)',
  position: 'relative',
  zIndex: 1,
  
  '@media (max-width: 768px)': {
    padding: '0 var(--spacing-md)'
  }
});

// 区块标题
const SectionTitle = styled('h2', {
  fontSize: 'var(--font-size-xxl)',
  fontWeight: 'var(--font-weight-bold)',
  textAlign: 'center',
  marginBottom: 'var(--spacing-xl)',
  color: '#2B3B4E',
  
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-xl)',
    marginBottom: 'var(--spacing-lg)'
  }
});

// 步骤容器
const StepsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: 'var(--spacing-xxl)',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
    maxWidth: '100%'
  }
});

// 单个步骤样式
const StepCard = styled(motion.div, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  width: '33.33%',
  padding: 'var(--spacing-md)',
  textAlign: 'center',
  
  '@media (max-width: 768px)': {
    flexDirection: 'row',
    width: '100%',
    gap: 'var(--spacing-md)',
    padding: 'var(--spacing-md)',
    textAlign: 'left',
    alignItems: 'flex-start',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E9ECEF'
  }
});

// 步骤图标容器
const StepIconContainer = styled('div', {
  width: '120px',
  height: '120px',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 'var(--spacing-md)',
  
  '@media (max-width: 768px)': {
    width: '80px',
    height: '80px',
    flexShrink: 0,
    marginBottom: 0,
    marginRight: 'var(--spacing-sm)'
  }
});

// 图标样式
const StepIcon = styled('img', {
  width: '120%',
  height: '120%',
  objectFit: 'contain',
  
  '@media (max-width: 768px)': {
    width: '100%',
    height: '100%'
  }
});


// 图标样式2
const StepIcon2 = styled('img', {
  width: '180%',
  height: '180%',
  objectFit: 'contain',
  
  '@media (max-width: 768px)': {
    width: '100%',
    height: '100%'
  }
});

// 文本内容容器
const TextContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  
  '@media (max-width: 768px)': {
    textAlign: 'left',
    flex: 1,
    justifyContent: 'flex-start'
  }
});

// 步骤标题样式
const StepTitle = styled('h3', {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: '#2B3B4E',
  marginBottom: 'var(--spacing-sm)',
  
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-lg)',
    marginBottom: 'var(--spacing-xs)',
    lineHeight: 1.3
  }
});

// 步骤描述样式
const StepDescription = styled('p', {
  fontSize: 'var(--font-size-md)',
  color: '#4A5568',
  lineHeight: 1.6,
  maxWidth: '280px',
  
  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.5,
    maxWidth: '100%',
    color: '#6B7280'
  }
});

/**
 * 如何成为矿工部分组件
 * 展示用户成为矿工的步骤流程
 */
const HowToMineSection: React.FC = () => {
  const { t } = useTranslation(); // 使用国际化钩子
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  // 步骤数据 - 使用国际化文本
  const steps = [
    {
      icon: '/images/howto1.png',
      title: t('home.howToMine.step1.title'),
      description: t('home.howToMine.step1.description')
    },
    {
      icon: '/images/howto2.png',
      title: t('home.howToMine.step2.title'),
      description: t('home.howToMine.step2.description')
    },
    {
      icon: '/images/howto3.png',
      title: t('home.howToMine.step3.title'),
      description: t('home.howToMine.step3.description')
    }
  ];
  
  // 卡片动画变体
  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.1 * i,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };
  
  // 移动端卡片动画变体
  const mobileCardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.15 * i,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };
  
  return (
    <SectionContainer id="how-to-mine">
      <ContentContainer>
        <SectionTitle>{t('home.howToMine.title')}</SectionTitle>
        
        <StepsContainer>
          {steps.map((step, index) => (
            <StepCard 
              key={index}
              variants={isMobile ? mobileCardVariants : cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={index}
              whileHover={isMobile ? { 
                scale: 1.02,
                transition: { duration: 0.3 }
              } : { 
                y: -5, 
                transition: { duration: 0.3 }
              }}
            >
              <StepIconContainer>
                {index === 1 ? (
                  <StepIcon2 src={step.icon} alt={step.title} />
                ) : (
                  <StepIcon src={step.icon} alt={step.title} />
                )}
              </StepIconContainer>
              <TextContainer>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </TextContainer>
            </StepCard>
          ))}
        </StepsContainer>
      </ContentContainer>
    </SectionContainer>
  );
};

export default HowToMineSection; 